import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { flashcardAPI, groupAPI, tagAPI, categoryAPI } from '../services/api';
import { useToast } from '../components/Toast';

const ManageFlashcards = () => {
  const { showToast } = useToast();
  const [flashcards, setFlashcards] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [currentFlashcard, setCurrentFlashcard] = useState(null);
  const [terms, setTerms] = useState([]);
  
  // Form states
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    groups: [],
    tags: [],
    category: null
  });
  
  // Term form state
  const [termForm, setTermForm] = useState({
    term: '',
    definition: ''
  });
  
  // AI generation state
  const [aiKeywords, setAiKeywords] = useState('');
  const [aiCount, setAiCount] = useState(10);
  const [generatedTerms, setGeneratedTerms] = useState([]);
  const [selectedTermIndices, setSelectedTermIndices] = useState([]);
  const [generatingAI, setGeneratingAI] = useState(false);

  useEffect(() => {
    fetchFlashcards();
    fetchGroups();
    fetchTags();
    fetchCategories();
  }, []);

  const fetchFlashcards = async () => {
    try {
      setLoading(true);
      const response = await flashcardAPI.getAllFlashcards();
      const flashcardsData = response.data || [];
      // Ensure each flashcard has groups as an array
      const normalizedFlashcards = flashcardsData.map(fc => ({
        ...fc,
        groups: Array.isArray(fc.groups) ? fc.groups : []
      }));
      setFlashcards(normalizedFlashcards);
    } catch (err) {
      showToast('Failed to fetch flashcards', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await groupAPI.getMyGroups();
      console.log('Groups response:', response.data);
      const groupsData = Array.isArray(response.data.groups) ? response.data.groups : [];
      setGroups(groupsData);
    } catch (err) {
      showToast('Failed to fetch groups', 'error');
      console.error('Error fetching groups:', err);
      setGroups([]); // Ensure groups is always an array
    }
  };

  const fetchTags = async () => {
    try {
      const response = await tagAPI.getAllTags();
      setTags(response.data.tags || []);
    } catch (err) {
      showToast('Failed to fetch tags', 'error');
      console.error('Error fetching tags:', err);
      setTags([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getCategoryTree();
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
    }
  };

  const handleCreateFlashcard = async (e) => {
    e.preventDefault();
    try {
      await flashcardAPI.createFlashcard(formData);
      closeCreateModal();
      fetchFlashcards();
      showToast('Flashcard created successfully', 'success');
    } catch (err) {
      showToast('Failed to create flashcard', 'error');
      console.error(err);
    }
  };

  const handleUpdateFlashcard = async (e) => {
    e.preventDefault();
    try {
      await flashcardAPI.updateFlashcard(currentFlashcard._id, formData);
      closeEditModal();
      fetchFlashcards();
      showToast('Flashcard updated successfully', 'success');
    } catch (err) {
      showToast('Failed to update flashcard', 'error');
      console.error(err);
    }
  };

  const handleDeleteFlashcard = async (id) => {
    if (!window.confirm('Are you sure you want to delete this flashcard? All associated terms will be deleted.')) {
      return;
    }
    try {
      await flashcardAPI.deleteFlashcard(id);
      fetchFlashcards();
      showToast('Flashcard deleted successfully', 'success');
    } catch (err) {
      showToast('Failed to delete flashcard', 'error');
      console.error(err);
    }
  };

  const openEditModal = (flashcard) => {
    setCurrentFlashcard(flashcard);
    setFormData({
      title: flashcard.title,
      description: flashcard.description,
      groups: (flashcard.groups && Array.isArray(flashcard.groups)) ? flashcard.groups.map(g => g._id) : [],
      tags: (flashcard.tags && Array.isArray(flashcard.tags)) ? flashcard.tags.map(t => t._id) : [],
      category: flashcard.category?._id || null
    });
    setShowEditModal(true);
  };

  const openCreateModal = () => {
    setFormData({ title: '', description: '', groups: [], tags: [], category: null });
    setCurrentFlashcard(null);
    setShowCreateModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setCurrentFlashcard(null);
    setFormData({ title: '', description: '', groups: [], tags: [], category: null });
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setFormData({ title: '', description: '', groups: [], tags: [], category: null });
  };

  const openTermsModal = async (flashcard) => {
    setCurrentFlashcard(flashcard);
    setShowTermsModal(true);
    try {
      const response = await flashcardAPI.getFlashcard(flashcard._id);
      setTerms(response.data.terms);
    } catch (err) {
      showToast('Failed to fetch terms', 'error');
      console.error(err);
    }
  };

  const handleAddTerm = async (e) => {
    e.preventDefault();
    try {
      await flashcardAPI.addTerm(currentFlashcard._id, termForm);
      setTermForm({ term: '', definition: '' });
      // Refresh terms
      const response = await flashcardAPI.getFlashcard(currentFlashcard._id);
      setTerms(response.data.terms);
      showToast('Term added successfully', 'success');
    } catch (err) {
      showToast('Failed to add term', 'error');
      console.error(err);
    }
  };

  const handleDeleteTerm = async (termId) => {
    if (!window.confirm('Are you sure you want to delete this term?')) {
      return;
    }
    try {
      await flashcardAPI.deleteTerm(currentFlashcard._id, termId);
      // Refresh terms
      const response = await flashcardAPI.getFlashcard(currentFlashcard._id);
      setTerms(response.data.terms);
      showToast('Term deleted successfully', 'success');
    } catch (err) {
      showToast('Failed to delete term', 'error');
      console.error(err);
    }
  };

  const handleGenerateTerms = async () => {
    if (!aiKeywords.trim()) {
      showToast('Please enter keywords for AI generation', 'error');
      return;
    }
    
    try {
      setGeneratingAI(true);
      const response = await flashcardAPI.generateTerms(currentFlashcard._id, {
        keywords: aiKeywords,
        count: aiCount
      });
      setGeneratedTerms(response.data.terms);
      setSelectedTermIndices(response.data.terms.map((_, idx) => idx)); // Select all by default
      showToast(`Generated ${response.data.terms.length} terms successfully`, 'success');
    } catch (err) {
      showToast('Failed to generate terms with AI: ' + (err.response?.data?.message || err.message), 'error');
      console.error(err);
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleAddGeneratedTerms = async () => {
    const selectedTerms = generatedTerms.filter((_, idx) => selectedTermIndices.includes(idx));
    if (selectedTerms.length === 0) {
      showToast('Please select at least one term to add', 'error');
      return;
    }
    
    try {
      await flashcardAPI.bulkAddTerms(currentFlashcard._id, selectedTerms);
      setGeneratedTerms([]);
      setSelectedTermIndices([]);
      setAiKeywords('');
      // Refresh terms
      const response = await flashcardAPI.getFlashcard(currentFlashcard._id);
      setTerms(response.data.terms);
      showToast(`Added ${selectedTerms.length} terms successfully`, 'success');
    } catch (err) {
      showToast('Failed to add generated terms', 'error');
      console.error(err);
    }
  };

  const toggleTermSelection = (index) => {
    setSelectedTermIndices(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTermIndices.length === generatedTerms.length) {
      setSelectedTermIndices([]);
    } else {
      setSelectedTermIndices(generatedTerms.map((_, idx) => idx));
    }
  };

  const handleGroupChange = (selectedOptions) => {
    setFormData(prev => ({
      ...prev,
      groups: selectedOptions ? selectedOptions.map(option => option.value) : []
    }));
  };

  const handleTagChange = async (selectedOptions, actionMeta) => {
    // Handle tag creation
    if (actionMeta.action === 'create-option') {
      const newTagName = actionMeta.option.label;
      try {
        const response = await tagAPI.createTag(newTagName);
        const newTag = response.data.tag;
        // Add to tags list
        setTags(prev => [...prev, newTag]);
        // Update form data with the new tag - map selectedOptions to IDs
        const updatedTags = selectedOptions.map(option => 
          option.__isNew__ ? newTag._id : option.value
        );
        setFormData(prev => ({
          ...prev,
          tags: updatedTags
        }));
        showToast(`Tag "${newTagName}" created successfully`, 'success');
      } catch (err) {
        showToast('Failed to create tag: ' + (err.response?.data?.message || err.message), 'error');
        console.error('Error creating tag:', err);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        tags: selectedOptions ? selectedOptions.map(option => option.value) : []
      }));
    }
  };

  // Get selected group options for react-select
  const getSelectedGroupOptions = () => {
    if (!groups || groups.length === 0 || !formData.groups || formData.groups.length === 0) {
      return [];
    }
    return groups
      .filter(g => formData.groups.includes(g._id))
      .map(g => ({ value: g._id, label: g.name }));
  };

  // Get all group options for react-select
  const getGroupOptions = () => {
    if (!groups || groups.length === 0) return [];
    return groups.map(g => ({ value: g._id, label: g.name }));
  };

  // Get selected tag options for react-select
  const getSelectedTagOptions = () => {
    if (!Array.isArray(tags) || tags.length === 0 || !formData.tags || formData.tags.length === 0) {
      return [];
    }
    return tags
      .filter(t => formData.tags.includes(t._id))
      .map(t => ({ value: t._id, label: t.name }));
  };

  // Get all tag options for react-select
  const getTagOptions = () => {
    if (!Array.isArray(tags) || tags.length === 0) return [];
    return tags.map(t => ({ value: t._id, label: t.name }));
  };

  // Get category options (flat list of all categories including nested)
  const getAllCategoriesFlat = (cats, parentName = null) => {
    let flat = [];
    cats.forEach(cat => {
      flat.push({ ...cat, parentName });
      if (cat.children && cat.children.length > 0) {
        flat = flat.concat(getAllCategoriesFlat(cat.children, cat.name));
      }
    });
    return flat;
  };

  const getCategoryOptions = () => {
    const flatCategories = getAllCategoriesFlat(categories);
    return flatCategories.map(cat => ({ 
      value: cat._id, 
      label: cat.parentName ? `${cat.parentName} → ${cat.name}` : cat.name 
    }));
  };

  const getSelectedCategoryOption = () => {
    if (!formData.category || !Array.isArray(categories) || categories.length === 0) {
      return null;
    }
    const flatCategories = getAllCategoriesFlat(categories);
    const selectedCat = flatCategories.find(cat => cat._id === formData.category);
    if (!selectedCat) return null;
    return {
      value: selectedCat._id,
      label: selectedCat.parentName ? `${selectedCat.parentName} → ${selectedCat.name}` : selectedCat.name
    };
  };

  const handleCategoryChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      category: selectedOption ? selectedOption.value : null
    }));
  };

  // Filter flashcards
  const filteredFlashcards = flashcards.filter(fc => {
    const matchesSearch = fc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         fc.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGroup = filterGroup === 'all' || (Array.isArray(fc.groups) && fc.groups.some(g => g._id === filterGroup));
    return matchesSearch && matchesGroup;
  });

  if (loading) {
    return <div className="loading">Loading flashcards...</div>;
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>🎴 Manage Flashcards</h2>
            <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
              {flashcards.length} flashcards created
            </p>
          </div>
          <button 
            onClick={openCreateModal}
            style={{
              padding: '10px 20px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#5568d3'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#667eea'}
          >
            + Create Flashcard
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'grid', gap: '12px', marginBottom: '20px', gridTemplateColumns: '2fr 1fr' }}>
          <input
            type="text"
            placeholder="🔍 Search flashcards..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '12px 16px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
          <select
            value={filterGroup}
            onChange={(e) => setFilterGroup(e.target.value)}
            style={{
              padding: '12px 16px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
              backgroundColor: 'white'
            }}
          >
            <option value="all">All Groups</option>
            {Array.isArray(groups) && groups.map(group => (
              <option key={group._id} value={group._id}>
                {group.name}
              </option>
            ))}
          </select>
        </div>

        {filteredFlashcards.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: '#718096', fontSize: '14px' }}>
            <p style={{ marginBottom: '8px', fontSize: '15px' }}>
              {searchTerm || filterGroup !== 'all' ? 'No flashcards found matching your filters' : 'No flashcards created yet'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {filteredFlashcards.map(flashcard => (
              <div key={flashcard._id} style={{
                backgroundColor: '#f7fafc',
                borderRadius: '10px',
                padding: '18px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ 
                  marginBottom: '12px', 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ 
                      margin: '0 0 8px 0', 
                      color: '#2d3748', 
                      fontSize: '16px', 
                      fontWeight: '600' 
                    }}>
                      {flashcard.title}
                    </h3>
                    <p style={{ 
                      margin: '0 0 12px 0', 
                      color: '#718096', 
                      fontSize: '14px', 
                      lineHeight: '1.5' 
                    }}>
                      {flashcard.description}
                    </p>
                    {flashcard.groups && flashcard.groups.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {flashcard.groups.map(group => (
                          <span key={group._id} style={{
                            fontSize: '12px',
                            color: '#667eea',
                            backgroundColor: '#eef2ff',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontWeight: '500'
                          }}>
                            👥 {group.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <button
                      onClick={() => openTermsModal(flashcard)}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        backgroundColor: '#48bb78',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      📝 Manage Terms
                    </button>
                    <button
                      onClick={() => openEditModal(flashcard)}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        backgroundColor: '#805ad5',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteFlashcard(flashcard._id)}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        backgroundColor: '#f56565',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeCreateModal}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px 0', color: '#2d3748', fontSize: '20px' }}>Create Flashcard</h3>
            <form onSubmit={handleCreateFlashcard}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '600', fontSize: '14px' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '600', fontSize: '14px' }}>
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '600', fontSize: '14px' }}>
                  Assign to Groups
                </label>
                <Select
                  isMulti
                  value={getSelectedGroupOptions()}
                  onChange={handleGroupChange}
                  options={getGroupOptions()}
                  placeholder="Select groups..."
                  isClearable={true}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: '#e2e8f0',
                      borderWidth: '1.5px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#667eea'
                      }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#667eea',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: 'white',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#5568d3',
                        color: 'white',
                      }
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 1050 })
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '600', fontSize: '14px' }}>
                  Assign Tags
                </label>
                <CreatableSelect
                  isMulti
                  value={getSelectedTagOptions()}
                  onChange={handleTagChange}
                  options={getTagOptions()}
                  placeholder="Select or create tags..."
                  isClearable={true}
                  formatCreateLabel={(inputValue) => `Create tag "${inputValue}"`}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: '#e2e8f0',
                      borderWidth: '1.5px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#667eea'
                      }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#667eea',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: 'white',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#5568d3',
                        color: 'white',
                      }
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 1050 })
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '600', fontSize: '14px' }}>
                  Select Category
                </label>
                <Select
                  value={getSelectedCategoryOption()}
                  onChange={handleCategoryChange}
                  options={getCategoryOptions()}
                  placeholder="Select category..."
                  isClearable={true}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: '#e2e8f0',
                      borderWidth: '1.5px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#667eea'
                      }
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#667eea' : state.isFocused ? '#f7fafc' : 'white',
                      color: state.isSelected ? 'white' : '#2d3748',
                      cursor: 'pointer'
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 1050 })
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button 
                  type="button" 
                  onClick={closeCreateModal}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#cbd5e0',
                    color: '#2d3748',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeEditModal}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px 0', color: '#2d3748', fontSize: '20px' }}>Edit Flashcard</h3>
            <form onSubmit={handleUpdateFlashcard}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '600', fontSize: '14px' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '600', fontSize: '14px' }}>
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '600', fontSize: '14px' }}>
                  Assign to Groups
                </label>
                <Select
                  isMulti
                  value={getSelectedGroupOptions()}
                  onChange={handleGroupChange}
                  options={getGroupOptions()}
                  placeholder="Select groups..."
                  isClearable={true}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: '#e2e8f0',
                      borderWidth: '1.5px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#667eea'
                      }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#667eea',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: 'white',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#5568d3',
                        color: 'white',
                      }
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 1050 })
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '600', fontSize: '14px' }}>
                  Assign Tags
                </label>
                <CreatableSelect
                  isMulti
                  value={getSelectedTagOptions()}
                  onChange={handleTagChange}
                  options={getTagOptions()}
                  placeholder="Select or create tags..."
                  isClearable={true}
                  formatCreateLabel={(inputValue) => `Create tag "${inputValue}"`}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: '#e2e8f0',
                      borderWidth: '1.5px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#667eea'
                      }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#667eea',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: 'white',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#5568d3',
                        color: 'white',
                      }
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 1050 })
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '600', fontSize: '14px' }}>
                  Select Category
                </label>
                <Select
                  value={getSelectedCategoryOption()}
                  onChange={handleCategoryChange}
                  options={getCategoryOptions()}
                  placeholder="Select category..."
                  isClearable={true}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: '#e2e8f0',
                      borderWidth: '1.5px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#667eea'
                      }
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#667eea' : state.isFocused ? '#f7fafc' : 'white',
                      color: state.isSelected ? 'white' : '#2d3748',
                      cursor: 'pointer'
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 1050 })
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button 
                  type="button" 
                  onClick={closeEditModal}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#cbd5e0',
                    color: '#2d3748',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Terms Management Modal */}
      {showTermsModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowTermsModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '900px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px 0', color: '#2d3748', fontSize: '20px' }}>
              Manage Terms - {currentFlashcard?.title}
            </h3>
            
            {/* AI Generation Section */}
            <div style={{
              background: '#f7fafc',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#2d3748', fontSize: '16px', fontWeight: '600' }}>
                🤖 AI Term Generation
              </h4>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '15px' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '600', fontSize: '14px' }}>
                    Keywords
                  </label>
                  <input
                    type="text"
                    value={aiKeywords}
                    onChange={(e) => setAiKeywords(e.target.value)}
                    placeholder="e.g., JavaScript, React, Hooks"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
                <div style={{ minWidth: '150px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '600', fontSize: '14px' }}>
                    Number of Terms
                  </label>
                  <input
                    type="number"
                    value={aiCount}
                    onChange={(e) => setAiCount(parseInt(e.target.value))}
                    min="1"
                    max="50"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleGenerateTerms}
                  disabled={generatingAI}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: generatingAI ? '#cbd5e0' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: generatingAI ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  {generatingAI ? 'Generating...' : 'Generate Terms'}
                </button>
              </div>

              {/* Generated Terms Preview */}
              {generatedTerms.length > 0 && (
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: 'white',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h5 style={{ margin: 0, color: '#2d3748', fontSize: '14px', fontWeight: '600' }}>
                      Generated Terms ({selectedTermIndices.length} of {generatedTerms.length} selected)
                    </h5>
                    <button
                      type="button"
                      onClick={toggleSelectAll}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#e2e8f0',
                        color: '#2d3748',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}
                    >
                      {selectedTermIndices.length === generatedTerms.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '15px' }}>
                    {generatedTerms.map((term, index) => (
                      <div 
                        key={index} 
                        style={{
                          padding: '12px',
                          borderLeft: `3px solid ${selectedTermIndices.includes(index) ? '#667eea' : '#cbd5e0'}`,
                          background: selectedTermIndices.includes(index) ? '#f7fafc' : '#fafafa',
                          marginBottom: '10px',
                          borderRadius: '4px',
                          display: 'flex',
                          gap: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => toggleTermSelection(index)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTermIndices.includes(index)}
                          onChange={() => toggleTermSelection(index)}
                          style={{ 
                            cursor: 'pointer',
                            width: '18px',
                            height: '18px',
                            marginTop: '2px',
                            flexShrink: 0
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div style={{ flex: 1 }}>
                          <strong style={{ display: 'block', marginBottom: '5px', color: '#2d3748', fontSize: '14px' }}>
                            {term.term}
                          </strong>
                          <p style={{ margin: 0, color: '#718096', fontSize: '13px', lineHeight: '1.5' }}>
                            {term.definition}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                      onClick={handleAddGeneratedTerms}
                      disabled={selectedTermIndices.length === 0}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: selectedTermIndices.length === 0 ? '#cbd5e0' : '#48bb78',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: selectedTermIndices.length === 0 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      Add Selected Terms ({selectedTermIndices.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setGeneratedTerms([]);
                        setSelectedTermIndices([]);
                      }}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#f56565',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              )}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '20px 0' }} />

            {/* Manual Term Addition */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#2d3748', fontSize: '16px', fontWeight: '600' }}>
                Add Term Manually
              </h4>
              <form onSubmit={handleAddTerm}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '600', fontSize: '14px' }}>
                    Term *
                  </label>
                  <input
                    type="text"
                    value={termForm.term}
                    onChange={(e) => setTermForm({ ...termForm, term: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '600', fontSize: '14px' }}>
                    Definition *
                  </label>
                  <textarea
                    value={termForm.definition}
                    onChange={(e) => setTermForm({ ...termForm, definition: e.target.value })}
                    required
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
                <button 
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Add Term
                </button>
              </form>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '20px 0' }} />

            {/* Existing Terms List */}
            <div>
              <h4 style={{ margin: '0 0 15px 0', color: '#2d3748', fontSize: '16px', fontWeight: '600' }}>
                Existing Terms ({terms.length})
              </h4>
              {terms.length === 0 ? (
                <p style={{ color: '#a0aec0', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                  No terms added yet. Add terms manually or use AI generation.
                </p>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '15px'
                }}>
                  {terms.map(term => (
                    <div key={term._id} style={{
                      background: '#f7fafc',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        marginBottom: '8px'
                      }}>
                        <strong style={{ color: '#2d3748', fontSize: '14px' }}>{term.term}</strong>
                        <button
                          onClick={() => handleDeleteTerm(term._id)}
                          style={{
                            background: '#f56565',
                            color: 'white',
                            border: 'none',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            fontSize: '16px',
                            lineHeight: '1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <p style={{ margin: 0, color: '#718096', fontSize: '13px', lineHeight: '1.5' }}>
                        {term.definition}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button 
                onClick={() => setShowTermsModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#cbd5e0',
                  color: '#2d3748',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ManageFlashcards;
